import React, { useEffect, useMemo, useState, useRef } from "react";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import { formatAmount, getAllMonths, getAllYears } from "../../../../services/appService";
import { useDispatch, useSelector } from "react-redux";
import { getMonthlyGrossProfitCandidates, loadMonthlyGrossProfitCandidates } from "../../../store/reports";
import GeneratePdf from "../../../../components/GeneratePdf";

const GrossProfitCandidates = () => {
    const years = getAllYears();
    const months = getAllMonths();

    const candidateEntity = [
        { id: 1, name: "C2C Basis" },
        { id: 2, name: "W2 Basis" },
        { id: 3, name: "1099 Basis" },
    ];

    const [pdfShowHide, setPdfShowHide] = useState(false);
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);

    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");
    const [selectedEntity, setSelectedEntity] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    // Live search and scroll states
    const [tableSearchTerm, setTableSearchTerm] = useState("");
    const [tableWidth, setTableWidth] = useState(0);
    const topScrollRef = useRef(null);
    const tableWrapperRef = useRef(null);

    const monthlyGrossProfitSelector = useSelector(getMonthlyGrossProfitCandidates);
    const [monthlyGrossProfit, setMonthlyGrossProfit] = useState([]);
    const dispatch = useDispatch();

    useEffect(() => {
        setMonthlyGrossProfit(monthlyGrossProfitSelector || []);
        setIsLoading(false);
    }, [monthlyGrossProfitSelector]);

    const handleYearChange = ({ currentTarget }) => {
        setYear(currentTarget.value);
        setMonthlyGrossProfit([]);
        setHasFetched(false);
    };

    const handleMonthChange = ({ currentTarget }) => {
        setMonth(currentTarget.value);
        setMonthlyGrossProfit([]);
        setHasFetched(false);
    };

    const GenerateGrossProfitRpt = () => {
        if (!year || !month) return;
        setIsLoading(true);
        setHasFetched(true);
        dispatch(loadMonthlyGrossProfitCandidates(year, month));
        setTableSearchTerm("");
    };

    // Filter Data by Entity and Search Term
    const filteredReport = useMemo(() => {
        if (!monthlyGrossProfit) return [];
        return monthlyGrossProfit.filter((item) => {
            // Filter by entity type
            if (selectedEntity !== "" && item.candidateType !== selectedEntity) {
                return false;
            }

            // Filter by search term
            const term = tableSearchTerm.toLowerCase();
            if (!term) return true;

            const name = `${item.candidateName || ""} ${item.candidateLastName || ""}`.toLowerCase();
            const candId = String(item.candidateId || "").toLowerCase();
            const type = (item.candidateType || "").toLowerCase();
            const sub = (item.subcontractor || "").toLowerCase();
            const rec = (item.recruiter || "").toLowerCase();

            return name.includes(term) ||
                   candId.includes(term) ||
                   type.includes(term) ||
                   sub.includes(term) ||
                   rec.includes(term);
        });
    }, [monthlyGrossProfit, selectedEntity, tableSearchTerm]);

    // Totals Calculation
    const totals = useMemo(() => {
        return filteredReport.reduce(
            (acc, item) => {
                acc.invoice += parseFloat(item.agencyAmount) || 0;
                acc.received += parseFloat(item.amountReceived) || 0;
                acc.candidate += parseFloat(item.candidateAmount) || 0;
                acc.subcontractor += parseFloat(item.subonctractorAmount) || 0; // original typo preserved
                acc.recruiter += parseFloat(item.recruiterAmount) || 0;
                acc.gross += parseFloat(item.grossProfit) || 0;
                return acc;
            },
            {
                invoice: 0,
                received: 0,
                candidate: 0,
                subcontractor: 0,
                recruiter: 0,
                gross: 0,
            }
        );
    }, [filteredReport]);

    // KPI Metrics values
    const getKpiStats = () => {
        const totalPayables = totals.candidate + totals.subcontractor + totals.recruiter;
        return {
            totalInvoicedStr: "$" + formatAmount(totals.invoice),
            totalReceivedStr: "$" + formatAmount(totals.received),
            totalPayablesStr: "$" + formatAmount(totalPayables),
            totalGrossProfitStr: "$" + formatAmount(totals.gross),
        };
    };

    const { totalInvoicedStr, totalReceivedStr, totalPayablesStr, totalGrossProfitStr } = getKpiStats();

    // Totals table row
    const getTotalRow = () => (
        <tr className="ach-pay-total-row">
            <td colSpan={5} className="font-weight-bold text-right ach-pay-total-label">Total</td>
            <td className="font-weight-bold text-right ach-pay-total-value">{"$" + formatAmount(totals.invoice)}</td>
            <td className="font-weight-bold text-right ach-pay-total-value">{"$" + formatAmount(totals.received)}</td>
            <td className="font-weight-bold text-right ach-pay-total-value">{"$" + formatAmount(totals.candidate)}</td>
            <td className="font-weight-bold text-right ach-pay-total-value">{"$" + formatAmount(totals.subcontractor)}</td>
            <td className="font-weight-bold text-right ach-pay-total-value">{"$" + formatAmount(totals.recruiter)}</td>
            <td className="font-weight-bold text-right ach-pay-total-value" style={{ color: totals.gross >= 0 ? "#10b981" : "#ef4444" }}>
                {"$" + formatAmount(totals.gross)}
            </td>
        </tr>
    );

    // Generate PDF data
    const generateGrossProfitPdf = () => {
        const headerList = [
            [
                "#",
                "Candidate",
                "Entity",
                "Subcontractor",
                "Recruiter",
                "Invoiced",
                "Received",
                "Payable Candidate",
                "Payable Subcontractor",
                "Payable Recruiter",
                "Gross Profit",
            ],
        ];

        const rowList = filteredReport.map((item) => [
            item.candidateId,
            `${item.candidateName} ${item.candidateLastName || ""}`.trim(),
            item.candidateType,
            item.subcontractor,
            item.recruiter,
            "$" + formatAmount(item.agencyAmount),
            "$" + formatAmount(item.amountReceived),
            "$" + formatAmount(item.candidateAmount),
            "$" + formatAmount(item.subonctractorAmount),
            "$" + formatAmount(item.recruiterAmount),
            "$" + formatAmount(item.grossProfit),
        ]);

        // add totals row
        rowList.push([
            "Total",
            "",
            "",
            "",
            "",
            "$" + formatAmount(totals.invoice),
            "$" + formatAmount(totals.received),
            "$" + formatAmount(totals.candidate),
            "$" + formatAmount(totals.subcontractor),
            "$" + formatAmount(totals.recruiter),
            "$" + formatAmount(totals.gross),
        ]);

        setHeaders(headerList);
        setRows(rowList);
        setPdfShowHide(true);
    };

    const handlepdfShowHide = () => {
        setPdfShowHide(false);
        setHeaders([]);
        setRows([]);
    };

    // Scroll synchronization
    const handleTopScroll = () => {
        if (topScrollRef.current && tableWrapperRef.current) {
            tableWrapperRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        }
    };

    const handleTableScroll = () => {
        if (topScrollRef.current && tableWrapperRef.current) {
            topScrollRef.current.scrollLeft = tableWrapperRef.current.scrollLeft;
        }
    };

    useEffect(() => {
        const updateWidth = () => {
            const table = document.getElementById("gross_profit_table");
            if (table) {
                setTableWidth(table.scrollWidth);
            }
        };

        updateWidth();
        const timer = setTimeout(updateWidth, 500);

        window.addEventListener("resize", updateWidth);
        return () => {
            window.removeEventListener("resize", updateWidth);
            clearTimeout(timer);
        };
    }, [filteredReport, year, month]);

    return (
        <div className="ach-pay-container">
            <style>{`
                .ach-pay-container {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    color: #1e293b;
                    margin-bottom: 2rem;
                }

                /* KPI Metric cards */
                .ach-pay-kpi-row {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
                    gap: 16px;
                    margin-bottom: 24px;
                }

                .ach-pay-metric-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 14px 18px;
                    display: flex;
                    align-items: center;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.01);
                }

                .ach-pay-metric-icon {
                    width: 38px;
                    height: 38px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    margin-right: 14px;
                    background: #f1f5f9;
                    color: #475569;
                    flex-shrink: 0;
                }

                .ach-pay-metric-val {
                    font-size: 18px;
                    font-weight: 800;
                    color: #0f172a;
                    line-height: 1.1;
                    margin-bottom: 2px;
                }

                .ach-pay-metric-label {
                    font-size: 10.5px;
                    color: #64748b;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                }

                /* Controls Layout */
                .ach-pay-controls {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                    background-color: #fafbfc;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 12px 18px;
                }

                .ach-pay-controls-group {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .ach-pay-search-wrapper {
                    position: relative;
                    width: 240px;
                }

                .ach-pay-search-input {
                    width: 100%;
                    padding: 8px 12px 8px 34px;
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    font-size: 12.5px;
                    color: #1e293b;
                    transition: all 0.2s ease;
                }

                .ach-pay-search-input:focus {
                    outline: none;
                    border-color: #4f46e5;
                    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
                }

                .ach-pay-search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    font-size: 12px;
                }

                .ach-pay-filter-select {
                    height: 35px;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    padding: 0 10px;
                    font-size: 12.5px;
                    color: #475569;
                    background: #ffffff;
                    max-width: 180px;
                }

                .ach-pay-filter-select:focus {
                    outline: none;
                    border-color: #4f46e5;
                }

                .ach-pay-actions-btns {
                    display: flex;
                    align-items: center;
                }

                .ach-pay-actions-btns > * {
                    margin-right: 8px !important;
                }

                .ach-pay-actions-btns > *:last-child {
                    margin-right: 0 !important;
                }

                .ach-pay-btn {
                    height: 35px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 0 14px;
                    border-radius: 8px;
                    font-size: 12.5px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    border: 1px solid #cbd5e1;
                    background: #ffffff;
                    color: #475569;
                }

                .ach-pay-btn:hover:not(:disabled) {
                    background-color: #f8fafc;
                    color: #0f172a;
                    border-color: #94a3b8;
                }

                .ach-pay-btn:disabled {
                    background-color: #f1f5f9;
                    border-color: #e2e8f0;
                    color: #94a3b8;
                    cursor: not-allowed;
                }

                .ach-pay-btn-primary {
                    background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%) !important;
                    color: #ffffff !important;
                    border: 1px solid #4338ca !important;
                    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.15);
                    transition: all 0.2s ease;
                    padding: 0 16px;
                }

                .ach-pay-btn-primary:hover:not(:disabled) {
                    background: linear-gradient(135deg, #4338ca 0%, #3730a3 100%) !important;
                    border-color: #3730a3 !important;
                    box-shadow: 0 6px 16px rgba(79, 70, 229, 0.25);
                    transform: translateY(-1px);
                }

                .ach-pay-btn-primary:disabled {
                    background: #f1f5f9 !important;
                    border-color: #e2e8f0 !important;
                    color: #94a3b8 !important;
                    box-shadow: none !important;
                    cursor: not-allowed;
                }

                /* Table modifications */
                .ach-pay-table-wrapper {
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    overflow-x: auto;
                    overflow-y: hidden;
                    background: white;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.01);
                }

                .ach-pay-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 13px;
                    text-align: left;
                }

                .ach-pay-table th {
                    background-color: #f8fafc;
                    color: #475569;
                    font-weight: 700;
                    text-transform: uppercase;
                    font-size: 10.5px;
                    letter-spacing: 0.05em;
                    padding: 12px 16px;
                    border-bottom: 2px solid #e2e8f0;
                    white-space: nowrap;
                }

                .ach-pay-table td {
                    padding: 12px 16px;
                    border-bottom: 1px solid #f1f5f9;
                    color: #334155;
                    transition: background-color 0.15s ease;
                    white-space: nowrap;
                }

                .ach-pay-table tr:hover td {
                    background-color: #f8fafc;
                }

                .ach-pay-table td.col-amount {
                    color: #0f172a;
                    font-weight: 600;
                    text-align: right !important;
                }

                /* Total Payment row layout */
                .ach-pay-total-row {
                    background-color: rgba(79, 70, 229, 0.04) !important;
                    border-top: 2px solid #cbd5e1;
                    border-bottom: 2px solid #cbd5e1;
                }

                .ach-pay-total-row td {
                    color: #0f172a !important;
                    font-weight: 700 !important;
                    padding: 14px 16px !important;
                }

                .ach-pay-total-label {
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    font-size: 11px;
                    color: #475569 !important;
                    text-align: right !important;
                }

                .ach-pay-total-value {
                    font-size: 13px;
                    color: #4f46e5 !important;
                    font-weight: 800 !important;
                    background-color: rgba(79, 70, 229, 0.04) !important;
                    text-align: right !important;
                }

                /* Shimmer and Loading Animation */
                @keyframes shimmer {
                  0%   { background-position: -600px 0; }
                  100% { background-position: 600px 0; }
                }

                @keyframes loadingBar {
                  0%   { width: 0%; opacity: 1; }
                  70%  { width: 85%; opacity: 1; }
                  100% { width: 100%; opacity: 0; }
                }

                .wf-loading-bar {
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  height: 3px;
                  background: linear-gradient(90deg, #4f46e5, #0ea5e9, #4f46e5);
                  background-size: 200% 100%;
                  animation: loadingBar 1.6s ease-in-out infinite;
                  z-index: 10;
                }

                .skeleton-cell {
                  display: block;
                  height: 12px;
                  border-radius: 6px;
                  background: linear-gradient(
                    90deg,
                    #f1f5f9 25%,
                    #e2e8f0 50%,
                    #f1f5f9 75%
                  );
                  background-size: 600px 100%;
                  animation: shimmer 1.4s ease-in-out infinite;
                }

                .skeleton-cell.sk-short  { width: 40%; }
                .skeleton-cell.sk-medium { width: 65%; }
                .skeleton-cell.sk-long   { width: 85%; }

                .skeleton-row td {
                  padding: 14px 16px !important;
                }

                /* Enhanced empty state */
                .wf-empty-state {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  padding: 50px 24px;
                  text-align: center;
                  gap: 12px;
                }

                .wf-empty-icon {
                  width: 52px;
                  height: 52px;
                  border-radius: 14px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 22px;
                  background: linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(14, 165, 233, 0.06) 100%);
                  border: 1px solid rgba(79, 70, 229, 0.12);
                  color: #4f46e5;
                  margin-bottom: 4px;
                }

                .wf-empty-title {
                  font-size: 15px;
                  font-weight: 700;
                  color: #1e293b;
                  margin: 0;
                  width: 100%;
                  text-align: center;
                }

                .wf-empty-desc {
                  font-size: 13px;
                  color: #64748b;
                  margin: 0 auto;
                  line-height: 1.6;
                  text-align: center;
                  width: 100%;
                }
            `}</style>

            <div className="ach-pay-controls">
                <div className="ach-pay-controls-group">
                    <div className="ach-pay-search-wrapper">
                        <i className="fa fa-search ach-pay-search-icon"></i>
                        <input
                            type="text"
                            placeholder="Search by candidate, subcontractor..."
                            className="ach-pay-search-input"
                            value={tableSearchTerm}
                            onChange={(e) => setTableSearchTerm(e.target.value)}
                            disabled={monthlyGrossProfit.length === 0}
                        />
                    </div>

                    <select
                        className="ach-pay-filter-select"
                        value={year}
                        onChange={handleYearChange}
                    >
                        <option value="">Year</option>
                        {years.map((y) => (
                            <option key={y.name} value={y.name}>
                                {y.name}
                            </option>
                        ))}
                    </select>

                    <select
                        className="ach-pay-filter-select"
                        value={month}
                        onChange={handleMonthChange}
                    >
                        <option value="">Month</option>
                        {months.map((m) => (
                            <option key={m.id} value={m.id}>
                                {m.name}
                            </option>
                        ))}
                    </select>

                    <select
                        className="ach-pay-filter-select"
                        value={selectedEntity}
                        onChange={(e) => setSelectedEntity(e.target.value)}
                    >
                        <option value="">All Entities</option>
                        {candidateEntity.map((item) => (
                            <option key={item.id} value={item.name}>
                                {item.name}
                            </option>
                        ))}
                    </select>

                    <button
                        className="ach-pay-btn ach-pay-btn-primary"
                        disabled={!year || !month}
                        onClick={GenerateGrossProfitRpt}
                    >
                        <i className="fa fa-refresh" style={{ fontSize: "11px", marginRight: "6px" }}></i>
                        Generate Report
                    </button>
                </div>

                <div className="ach-pay-actions-btns">
                    <ReactHTMLTableToExcel
                        id="xls-btn"
                        className="ach-pay-btn"
                        table="gross_profit_table"
                        filename="Monthly_Gross_Profit"
                        sheet="Monthly_Gross_Profit"
                        buttonText="Excel Export"
                    />

                    <button
                        className="ach-pay-btn"
                        disabled={filteredReport.length === 0}
                        onClick={generateGrossProfitPdf}
                    >
                        <i className="fa fa-file-pdf-o" style={{ color: "#ef4444" }}></i>
                        Export PDF
                    </button>
                </div>
            </div>

            {!year || !month ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-calendar-o"></i>
                    </div>
                    <p className="wf-empty-title">No Date Chosen</p>
                    <p className="wf-empty-desc">Please choose a valid Year and Month in the controls above.</p>
                </div>
            ) : !hasFetched ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-play-circle-o" style={{ color: "#4f46e5" }}></i>
                    </div>
                    <p className="wf-empty-title">Ready to Load Data</p>
                    <p className="wf-empty-desc">Click the "Generate Report" button above to fetch gross profits for this period.</p>
                </div>
            ) : (
                <div className="ach-pay-report-body p-0">
                    {/* KPI Metrics */}
                    {monthlyGrossProfit.length > 0 && (
                        <div className="ach-pay-kpi-row" style={{ marginTop: "16px" }}>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon" style={{ background: "rgba(99, 102, 241, 0.08)", color: "#4f46e5" }}>
                                    <i className="fa fa-usd"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalInvoicedStr}</div>
                                    <div className="ach-pay-metric-label">Total Invoiced</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon" style={{ background: "rgba(16, 185, 129, 0.08)", color: "#10b981" }}>
                                    <i className="fa fa-check-circle-o"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalReceivedStr}</div>
                                    <div className="ach-pay-metric-label">Total Received</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon" style={{ background: "rgba(239, 68, 68, 0.08)", color: "#ef4444" }}>
                                    <i className="fa fa-exclamation-triangle"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalPayablesStr}</div>
                                    <div className="ach-pay-metric-label">Total Payables</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon" style={{ background: totals.gross >= 0 ? "rgba(16, 185, 129, 0.08)" : "rgba(239, 68, 68, 0.08)", color: totals.gross >= 0 ? "#10b981" : "#ef4444" }}>
                                    <i className="fa fa-line-chart"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val" style={{ color: totals.gross >= 0 ? "#10b981" : "#ef4444" }}>{totalGrossProfitStr}</div>
                                    <div className="ach-pay-metric-label">Net Gross Profit</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Synchronized top scrollbar */}
                    {monthlyGrossProfit.length > 0 && filteredReport.length > 0 && (
                        <div 
                            ref={topScrollRef}
                            onScroll={handleTopScroll}
                            style={{ overflowX: "auto", overflowY: "hidden", height: "14px", width: "100%", marginBottom: "4px" }}
                        >
                            <div style={{ width: `${tableWidth}px`, height: "1px" }}></div>
                        </div>
                    )}

                    <div 
                        ref={tableWrapperRef}
                        onScroll={handleTableScroll}
                        className="ach-pay-table-wrapper"
                        style={{ position: "relative" }}
                    >
                        {isLoading && <div className="wf-loading-bar" />}

                        {isLoading ? (
                            /* SKELETON TABLE LOADING SCREEN */
                            <table className="ach-pay-table">
                                <thead>
                                    <tr>
                                        <th colSpan={5}></th>
                                        <th colSpan={2} className="bg-white text-center">Accounts Receivable</th>
                                        <th colSpan={3} className="bg-lighter text-center">Accounts Payable</th>
                                        <th></th>
                                    </tr>
                                    <tr>
                                        <th>#</th>
                                        <th>Candidate</th>
                                        <th>Entity</th>
                                        <th>Subcontractor</th>
                                        <th>Recruiter</th>
                                        <th className="bg-white text-right">Amount Invoiced</th>
                                        <th className="bg-white text-right">Amount Received</th>
                                        <th className="bg-lighter text-right">Payable Candidate</th>
                                        <th className="bg-lighter text-right">Payable Subcontractor</th>
                                        <th className="bg-lighter text-right">Payable Recruiter</th>
                                        <th className="text-right">Gross Profit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <tr key={`skel-${i}`} className="skeleton-row">
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.06}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.08}s` }} /></td>
                                            <td className="bg-white"><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td className="bg-white"><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                            <td className="bg-lighter"><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td className="bg-lighter"><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.06}s` }} /></td>
                                            <td className="bg-lighter"><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.08}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.09}s` }} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : monthlyGrossProfit.length > 0 ? (
                            filteredReport.length > 0 ? (
                                <table
                                    className="ach-pay-table"
                                    id="gross_profit_table"
                                >
                                    <thead>
                                        <tr>
                                            <th colSpan={5}></th>
                                            <th colSpan={2} className="bg-white text-center" style={{ borderBottom: "2px solid #cbd5e1" }}>Accounts Receivable</th>
                                            <th colSpan={3} className="bg-lighter text-center" style={{ borderBottom: "2px solid #cbd5e1" }}>Accounts Payable</th>
                                            <th></th>
                                        </tr>

                                        <tr>
                                            <th>#</th>
                                            <th>Candidate</th>
                                            <th>Entity</th>
                                            <th>Subcontractor</th>
                                            <th>Recruiter</th>

                                            <th className="bg-white text-right">Amount Invoiced</th>
                                            <th className="bg-white text-right">Amount Received</th>

                                            <th className="bg-lighter text-right">Payable Candidate</th>
                                            <th className="bg-lighter text-right">Payable Subcontractor</th>
                                            <th className="bg-lighter text-right">Payable Recruiter</th>

                                            <th className="text-right">Gross Profit</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredReport.map((item) => (
                                            <tr key={item.candidateId}>
                                                <td>{item.candidateId}</td>
                                                <td>{item.candidateName} {item.candidateLastName}</td>
                                                <td>{item.candidateType}</td>
                                                <td>{item.subcontractor}</td>
                                                <td>{item.recruiter}</td>

                                                <td className="bg-white text-right col-amount">{"$" + formatAmount(item.agencyAmount)}</td>
                                                <td className="bg-white text-right col-amount">{"$" + formatAmount(item.amountReceived)}</td>
                                                <td className="bg-lighter text-right col-amount">{"$" + formatAmount(item.candidateAmount)}</td>
                                                <td className="bg-lighter text-right col-amount">{"$" + formatAmount(item.subonctractorAmount)}</td>
                                                <td className="bg-lighter text-right col-amount">{"$" + formatAmount(item.recruiterAmount)}</td>
                                                <td className="text-right col-amount font-weight-bold" style={{ color: parseFloat(item.grossProfit) >= 0 ? "#10b981" : "#ef4444" }}>
                                                    {"$" + formatAmount(item.grossProfit)}
                                                </td>
                                            </tr>
                                        ))}

                                        {getTotalRow()}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="wf-empty-state">
                                    <div className="wf-empty-icon">
                                        <i className="fa fa-search"></i>
                                    </div>
                                    <p className="wf-empty-title">No Matching Records</p>
                                    <p className="wf-empty-desc">No entries matched your search query. Try adjusting your search term.</p>
                                </div>
                            )
                        ) : (
                            <div className="wf-empty-state">
                                <div className="wf-empty-icon">
                                    <i className="fa fa-folder-open-o"></i>
                                </div>
                                <p className="wf-empty-title">No Profits Found</p>
                                <p className="wf-empty-desc">No gross profit records found for this period selection.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* PDF Modal */}
            <GeneratePdf
                handlepdfShowHide={handlepdfShowHide}
                pdfShowHide={pdfShowHide}
                selectedCol={headers}
                selectedColData={rows}
            />
        </div>
    );
};

export default GrossProfitCandidates;